import { defineStore } from "pinia";
import { ref, type Ref } from "vue";
import { api } from "@/app/services/api.service";

export const useConfigurationsStore = defineStore("configurations", () => {
  // Departments
  const departments: Ref<any> = ref(null);
  const department: Ref<any> = ref(null);

  // Occupations
  const occupations: Ref<any> = ref(null);
  const occupation: Ref<any> = ref(null);

  // Countries
  const countries: Ref<any> = ref(null);
  const country: Ref<any> = ref(null);
  const countryError: Ref<any> = ref(null);

  // User Manuals
  const manuals: Ref<any> = ref(null);
  const manualCreated: Ref<any> = ref(null);
  const manualDeleted: Ref<any> = ref(null);
  const manualsError: Ref<any> = ref(null);

  // Messenger
  const messageSentSuccess: Ref<any> = ref(null);

  // Dictionary
  const words: Ref<any[]> = ref([]);
  const dictionaries: Ref<any[]> = ref([]);
  const wordSuccess: Ref<any> = ref(null);
  const wordError: Ref<any> = ref(null);

  // Reservations
  const dictionaryAdded: Ref<any> = ref(null);
  const reservationExpiryTimes: Ref<any[]> = ref([]);
  const reservationAddStatus: Ref<any> = ref(null);
  const reservationAddError: Ref<any> = ref(null);

  // ISIC
  const isicList: Ref<any[]> = ref([]);
  const isicChildren: Ref<any[]> = ref([]);
  const isicAdd: Ref<any> = ref(null);
  const isicError: Ref<any> = ref(null);

  // Rejection/Query words
  const rejectionWords: Ref<any[]> = ref([]);
  const rejectionSuccess: Ref<any> = ref(null);
  const rejectionError: Ref<any> = ref(null);

  // Name search
  const searchResults: Ref<any[]> = ref([]);
  const nameFileUpload: Ref<any> = ref(null);
  const searchError: Ref<any> = ref(null);

  // Insolvency settings
  const professionalBodies: Ref<any> = ref(null);
  const insolvencyReasons: Ref<any> = ref(null);
  const academicQualifications: Ref<any> = ref(null);
  const professionalQualifications: Ref<any> = ref(null);
  const professionalBody: Ref<any> = ref(null);
  const insolvencyReason: Ref<any> = ref(null);
  const academicQualification: Ref<any> = ref(null);
  const professionalQualification: Ref<any> = ref(null);
  const insolvencies: Ref<any> = ref(null);
  const insolvencyError: Ref<any> = ref(null);

  // Winding up / task messages
  const windingDraftData: Ref<any> = ref(null);
  const windingDraftDataError: Ref<any> = ref(null);
  const insolvencyTaskMessages: Ref<any[]> = ref([]);

  const error: Ref<any> = ref(null);

  // --- Department actions ---
  const fetchDepartments = () => {
    api().get("auth/api/departments").then(r => {
      departments.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };
  const createDepartment = (data: any) => {
    department.value = null;
    api().post("auth/api/departments/", data).then(r => {
      department.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };

  // --- Occupation actions ---
  const fetchOccupations = () => {
    api().get("configuration/occupation/all").then(r => {
      occupations.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };
  const createOccupation = (data: any) => {
    occupation.value = null;
    api().post("configuration/occupation/create", data).then(r => {
      occupation.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };

  // --- Country actions ---
  const fetchCountries = () => {
    api().get("configuration/address/country").then(r => {
      countries.value = r.data;
    }).catch(e => {
      countryError.value = e.response?.data;
    });
  };
  const createCountry = (data: any) => {
    country.value = null;
    api().post("configuration/address/country", data).then(r => {
      country.value = r.data;
    }).catch(e => {
      countryError.value = e.response?.data;
    });
  };
  const updateCountry = (data: any) => {
    country.value = null;
    api().post("configuration/address/country", data).then(r => {
      country.value = r.data;
    }).catch(e => {
      countryError.value = e.response?.data;
    });
  };

  // --- User Manual actions ---
  const fetchManuals = () => {
    api().get("configuration/user-manual").then(r => {
      manuals.value = r.data;
    }).catch(e => {
      manualsError.value = e.response?.data;
    });
  };
  const createManual = (data: any) => {
    manualCreated.value = null;
    api().post("configuration/user-manual/add", data).then(r => {
      manualCreated.value = r.data;
    }).catch(e => {
      manualsError.value = e.response?.data;
    });
  };
  const deleteManual = (id: string) => {
    manualDeleted.value = null;
    api().get("configuration/user-manual/delete/" + id).then(r => {
      manualDeleted.value = r.data;
    }).catch(e => {
      manualsError.value = e.response?.data;
    });
  };

  // --- Messenger actions ---
  const sendMessage = (data: any) => {
    messageSentSuccess.value = null;
    api().post("configuration/system-messenger/send", data).then(r => {
      messageSentSuccess.value = r.data;
    }).catch(e => {
      error.value = e.response?.data;
    });
  };

  // --- Dictionary actions ---
  const fetchDictionaries = () => {
    api().get("/namesearch/dictionary").then(r => {
      dictionaries.value = r.data;
    }).catch(e => {
      wordError.value = e.response?.data;
    });
  };
  const fetchWords = () => {
    api().get("/namesearch/dictionary/word").then(r => {
      words.value = r.data;
    }).catch(e => {
      wordError.value = e.response?.data;
    });
  };
  const fetchFilteredWords = (id: string) => {
    api().get("/namesearch/dictionary/word?dictionaryId=" + id).then(r => {
      words.value = r.data;
    }).catch(e => {
      wordError.value = e.response?.data;
    });
  };
  const addWord = (data: any) => {
    wordSuccess.value = null;
    api().post("/namesearch/dictionary/word", data).then(r => {
      wordSuccess.value = r.data;
    }).catch(e => {
      wordError.value = e.response?.data;
    });
  };
  const editWord = (data: any) => {
    wordSuccess.value = null;
    api().put("/namesearch/dictionary/word?wordId=" + data.id, data.form).then(r => {
      wordSuccess.value = r.data;
    }).catch(e => {
      wordError.value = e.response?.data;
    });
  };
  const deleteWord = (data: any) => {
    wordSuccess.value = null;
    api().delete("/namesearch/dictionary/word?wordId=" + data.id, data).then(r => {
      wordSuccess.value = r.data;
    }).catch(e => {
      wordError.value = e.message;
    });
  };

  // --- Reservation/Dictionary actions ---
  const fetchDictionary = () => {
    api().get("namesearch/dictionary").then(r => {
      dictionaries.value = r.data;
    }).catch(e => {
      reservationAddError.value = e.response?.data;
    });
  };
  const addDictionary = (data: any) => {
    dictionaryAdded.value = null;
    api().post("namesearch/dictionary", data).then(r => {
      dictionaryAdded.value = r.data;
    }).catch(e => {
      reservationAddError.value = e.response?.data;
    });
  };
  const addReservationExpiryTime = (data: any) => {
    reservationAddStatus.value = null;
    api().post("reservation/settings/expiry", data).then(r => {
      reservationAddStatus.value = r.data;
    }).catch(e => {
      reservationAddError.value = e.response?.data;
    });
  };
  const fetchReservationExpiryTimes = () => {
    api().get("reservation/settings/expiry").then(r => {
      reservationExpiryTimes.value = r.data;
    }).catch(e => {
      reservationAddError.value = e.response?.data;
    });
  };
  const addReservationNotificationTime = (data: any) => {
    reservationAddStatus.value = null;
    api().post("reservation/settings/notifications", data).then(r => {
      reservationAddStatus.value = r.data;
    }).catch(e => {
      reservationAddError.value = e.response?.data;
    });
  };

  // --- ISIC actions ---
  const fetchIsic = () => {
    api().get("/reservation/isic").then(r => {
      isicList.value = r.data;
    }).catch(e => {
      isicError.value = e.response?.data;
    });
  };
  const fetchLevelOneIsic = () => {
    api().get("/reservation/isic/parents").then(r => {
      isicChildren.value = r.data;
    }).catch(e => {
      isicError.value = e.response?.data;
    });
  };
  const addIsicCategory = (data: any) => {
    isicAdd.value = null;
    api().post("/reservation/isic", data).then(r => {
      isicAdd.value = r.data;
    }).catch(e => {
      isicError.value = e.response?.data;
    });
  };

  // --- Rejection/Query word actions ---
  const fetchQueryRejectionWords = () => {
    api().get("/configuration/appeal-message/all").then(r => {
      rejectionWords.value = r.data;
    }).catch(e => {
      rejectionError.value = e.response?.data;
    });
  };
  const addRejectionQueryWord = (data: any) => {
    rejectionSuccess.value = null;
    api().post("/configuration/appeal-message/create/", data).then(r => {
      rejectionSuccess.value = r.data;
    }).catch(e => {
      rejectionError.value = e.response?.data;
    });
  };
  const editRejectionQueryWord = (data: any) => {
    rejectionSuccess.value = null;
    api().post("/configuration/appeal-message/edit", data).then(r => {
      rejectionSuccess.value = r.data;
    }).catch(e => {
      rejectionError.value = e.response?.data;
    });
  };
  const deleteRejectionQueryWord = (data: any) => {
    rejectionSuccess.value = null;
    api().get("/configuration/appeal-message/delete/" + data.id).then(r => {
      rejectionSuccess.value = r.data;
    }).catch(e => {
      rejectionError.value = e.message;
    });
  };

  // --- Name search actions ---
  const doSearch = (filters: string) => {
    searchError.value = null;
    api().get("/namesearch/search" + filters).then(r => {
      searchResults.value = r.data;
    }).catch(e => {
      searchError.value = e.response?.data;
    });
  };
  const uploadBusinessNames = (data: any) => {
    searchError.value = null;
    api().post("/namesearch/upload-names-file", data).then(r => {
      nameFileUpload.value = r.data;
    }).catch(e => {
      searchError.value = e.response?.data;
    });
  };

  // --- Insolvency settings actions ---
  const fetchProfessionalBodies = () => {
    api().get("configuration/professional-body/all").then(r => {
      professionalBodies.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const fetchReasons = () => {
    api().get("configuration/insolvency-reasons").then(r => {
      insolvencyReasons.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const fetchAllInsolvency = (params: string) => {
    api().get("/insolvency/process" + params).then(r => {
      insolvencies.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const fetchAcademicQualifications = () => {
    api().get("configuration/academic-qualification/all").then(r => {
      academicQualifications.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const fetchProfessionalQualifications = () => {
    api().get("configuration/professional-qualification/all").then(r => {
      professionalQualifications.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const createProfessionalBody = (data: any) => {
    professionalBody.value = null;
    api().post("configuration/professional-body/create", data).then(r => {
      professionalBody.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const createReason = (data: any) => {
    insolvencyReason.value = null;
    api().post("configuration/insolvency-reasons", data).then(r => {
      insolvencyReason.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const updateProfessionalBody = (data: any) => {
    professionalBody.value = null;
    api().post("configuration/professional-body/update", data).then(r => {
      professionalBody.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const createAcademicQualification = (data: any) => {
    academicQualification.value = null;
    api().post("configuration/academic-qualification/create", data).then(r => {
      academicQualification.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const updateAcademicQualification = (data: any) => {
    academicQualification.value = null;
    api().post("configuration/academic-qualification/update", data).then(r => {
      academicQualification.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const createProfessionalQualification = (data: any) => {
    professionalQualification.value = null;
    api().post("configuration/professional-qualification/create", data).then(r => {
      professionalQualification.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };
  const updateProfessionalQualification = (data: any) => {
    professionalQualification.value = null;
    api().post("configuration/professional-qualification/update", data).then(r => {
      professionalQualification.value = r.data;
    }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };

  // --- Winding up / task messages ---
  const fetchWindingDraftData = (trackingNumber: string) => {
    windingDraftData.value = null;
    windingDraftDataError.value = null;
    api().get("insolvency/winding-up/details?tracking-number=" + trackingNumber)
      .then(r => {
        windingDraftData.value = r.data;
      }).catch(e => {
      windingDraftDataError.value = e.response?.data;
    });
  };
  const fetchInsolvencyTaskMessages = (id: string) => {
    api().get("/insolvency/api/winding-up/task-messages/" + id)
      .then(r => {
        insolvencyTaskMessages.value = r.data?.messages ?? r.data ?? [];
      }).catch(e => {
      insolvencyError.value = e.response?.data;
    });
  };

  return {
    departments,
    department,
    occupations,
    occupation,
    countries,
    country,
    countryError,
    manuals,
    manualCreated,
    manualDeleted,
    manualsError,
    messageSentSuccess,
    words,
    dictionaries,
    wordSuccess,
    wordError,
    dictionaryAdded,
    reservationExpiryTimes,
    reservationAddStatus,
    reservationAddError,
    isicList,
    isicChildren,
    isicAdd,
    isicError,
    rejectionWords,
    rejectionSuccess,
    rejectionError,
    searchResults,
    nameFileUpload,
    searchError,
    professionalBodies,
    insolvencyReasons,
    academicQualifications,
    professionalQualifications,
    professionalBody,
    insolvencyReason,
    academicQualification,
    professionalQualification,
    insolvencies,
    insolvencyError,
    windingDraftData,
    windingDraftDataError,
    insolvencyTaskMessages,
    error,
    fetchDepartments,
    createDepartment,
    fetchOccupations,
    createOccupation,
    fetchCountries,
    createCountry,
    updateCountry,
    fetchManuals,
    createManual,
    deleteManual,
    sendMessage,
    fetchDictionaries,
    fetchWords,
    fetchFilteredWords,
    addWord,
    editWord,
    deleteWord,
    fetchDictionary,
    addDictionary,
    addReservationExpiryTime,
    fetchReservationExpiryTimes,
    addReservationNotificationTime,
    fetchIsic,
    fetchLevelOneIsic,
    addIsicCategory,
    fetchQueryRejectionWords,
    addRejectionQueryWord,
    editRejectionQueryWord,
    deleteRejectionQueryWord,
    doSearch,
    uploadBusinessNames,
    fetchProfessionalBodies,
    fetchReasons,
    fetchAllInsolvency,
    fetchAcademicQualifications,
    fetchProfessionalQualifications,
    createProfessionalBody,
    createReason,
    updateProfessionalBody,
    createAcademicQualification,
    updateAcademicQualification,
    createProfessionalQualification,
    updateProfessionalQualification,
    fetchWindingDraftData,
    fetchInsolvencyTaskMessages
  };
});
